/**
 * SalaryRateHistoryPanel — Central Management view (iter266).
 *
 * Filters: Center | Employee (name or id) | Effective Month | Status
 *          (Active / Historical / Reverted)
 *
 * Actions in-row are intentionally NOT rendered here (kept view-only).
 * SA/MGT should open the individual employee's Salary History modal
 * from the Employees screen for edit/delete/revert — this panel is
 * a read-only aggregate finder as spec'd.
 */
import { useEffect, useMemo, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { RefreshCw, Search, History } from "lucide-react";
import { toast } from "sonner";
import { api, fetchCentersFromDB } from "@/lib/api";

const _INR = (v) =>
  v == null || v === "" ? "-" :
  Number(v).toLocaleString("en-IN", { maximumFractionDigits: 2 });

const _STATUS_BADGE = {
  Active:     "bg-emerald-100 text-emerald-800 border-emerald-300",
  Historical: "bg-slate-100 text-slate-700 border-slate-300",
  Reverted:   "bg-amber-100 text-amber-800 border-amber-300",
};

export default function SalaryRateHistoryPanel({ session }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [centers, setCenters] = useState([]);
  const [filters, setFilters] = useState({
    center: "ALL",
    name: "",
    effective_from_month: "",
    status: "ALL",
  });

  useEffect(() => {
    (async () => {
      try {
        const c = await fetchCentersFromDB();
        setCenters(c || []);
      } catch { /* ignore */ }
    })();
  }, []);

  const load = async () => {
    if (!session?.token) return;
    setLoading(true);
    try {
      const body = { token: session.token };
      if (filters.center && filters.center !== "ALL") body.center = filters.center;
      if (filters.name) body.name = filters.name;
      if (filters.effective_from_month) body.effective_from_month = filters.effective_from_month;
      if (filters.status && filters.status !== "ALL") body.status = filters.status;
      const res = await api.post("/salary-history/all", body);
      setRows(res.data.rows || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load salary history");
    } finally {
      setLoading(false);
    }
  };

  // Auto-load on mount
  useEffect(() => { load(); /* eslint-disable-line */ }, []);

  const countBadge = useMemo(
    () => `${rows.length} entr${rows.length === 1 ? "y" : "ies"}`,
    [rows.length]
  );

  return (
    <Card className="mt-6" data-testid="salary-rate-history-panel">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <History className="w-5 h-5" />
          Salary Rate History
          <Badge variant="secondary" className="ml-2">{countBadge}</Badge>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
          <div className="space-y-1">
            <Label className="text-xs">Center</Label>
            <Select
              value={filters.center}
              onValueChange={(v) => setFilters({ ...filters, center: v })}
            >
              <SelectTrigger data-testid="srh-filter-center">
                <SelectValue placeholder="All Centers" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="ALL">All Centers</SelectItem>
                {(centers || []).map((c) => (
                  <SelectItem key={c.code || c} value={c.code || c}>
                    {c.code || c}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Employee (name)</Label>
            <Input
              placeholder="e.g. VISHAL NAND"
              value={filters.name}
              onChange={(e) => setFilters({ ...filters, name: e.target.value })}
              data-testid="srh-filter-name"
            />
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Effective Month</Label>
            <Input
              type="month"
              value={filters.effective_from_month}
              onChange={(e) => setFilters({ ...filters, effective_from_month: e.target.value })}
              data-testid="srh-filter-month"
            />
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Status</Label>
            <Select
              value={filters.status}
              onValueChange={(v) => setFilters({ ...filters, status: v })}
            >
              <SelectTrigger data-testid="srh-filter-status">
                <SelectValue placeholder="All" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="ALL">All</SelectItem>
                <SelectItem value="Active">Active</SelectItem>
                <SelectItem value="Historical">Historical</SelectItem>
                <SelectItem value="Reverted">Reverted</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1 flex flex-col justify-end">
            <Button
              onClick={load}
              disabled={loading}
              className="w-full"
              data-testid="srh-apply-filters"
            >
              {loading ? (
                <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Search className="w-4 h-4 mr-2" />
              )}
              Apply
            </Button>
          </div>
        </div>

        <div className="border rounded-md overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-muted/50 border-b">
                <th className="text-left p-2">Center</th>
                <th className="text-left p-2">Employee</th>
                <th className="text-left p-2">Effective</th>
                <th className="text-right p-2">Old → New</th>
                <th className="text-left p-2">Status</th>
                <th className="text-left p-2">Changed By</th>
                <th className="text-left p-2">Changed At</th>
                <th className="text-left p-2">Reason</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && !loading && (
                <tr>
                  <td colSpan={8} className="p-6 text-center text-muted-foreground">
                    No salary rate changes found for the selected filters.
                  </td>
                </tr>
              )}
              {rows.map((r) => (
                <tr
                  key={`${r.name_norm}-${r.effective_from_month}-${r.changed_at || ""}`}
                  className="border-b hover:bg-muted/30"
                  data-testid={`srh-row-${r.name_norm}-${r.effective_from_month}`}
                >
                  <td className="p-2">{r.center || "—"}</td>
                  <td className="p-2 font-medium">{r.name}</td>
                  <td className="p-2 font-mono">{r.effective_from_month}</td>
                  <td className="p-2 text-right">
                    <span className="text-muted-foreground">₹{_INR(r.old_salary)}</span>
                    <span className="mx-1">→</span>
                    <strong>₹{_INR(r.new_salary)}</strong>
                  </td>
                  <td className="p-2">
                    <Badge variant="outline" className={_STATUS_BADGE[r.status] || ""}>
                      {r.status}
                    </Badge>
                  </td>
                  <td className="p-2">{r.changed_by || "—"}</td>
                  <td className="p-2 text-xs">
                    {r.changed_at ? new Date(r.changed_at).toLocaleString() : "—"}
                  </td>
                  <td className="p-2 max-w-[220px] truncate" title={r.reason || ""}>
                    {r.reason || "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="text-xs text-muted-foreground">
          Read-only view. To edit / revert / delete a specific rate,
          open the employee from HR Management → Employees and use{" "}
          <em>View Salary History</em>.
        </p>
      </CardContent>
    </Card>
  );
}
